import React, { forwardRef, useState, useCallback, useMemo, useEffect } from 'react';
import clsx from 'clsx';
import { ChevronUpIcon, ChevronDownIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { CURRENCIES } from '../../../lib/currencies';

type NameType = 'firstName' | 'middleName' | 'lastName';

// Add default placeholders
const DEFAULT_PLACEHOLDERS = {
  email: 'example@email.com',
  url: 'https://www.example.com'
} as const;

const NAME_CONFIG = {
  firstName: { label: 'First Name', placeholder: 'Enter your first name' },
  middleName: { label: 'Middle Name', placeholder: 'Enter your middle name (optional)' },
  lastName: { label: 'Last Name', placeholder: 'Enter your last name' }
} as const;

interface NameInputContainerProps {
  children: React.ReactNode;
  className?: string;
  showLastName?: boolean;
}

export const NameInputContainer: React.FC<NameInputContainerProps> = ({
  children,
  className,
  showLastName = true
}) => {
  const filteredChildren = useMemo(() => {
    const childrenArray = React.Children.toArray(children);
    return showLastName 
      ? childrenArray 
      : childrenArray.filter(child => {
          if (React.isValidElement(child) && 'nameType' in (child.props as any)) {
            return (child.props as { nameType?: NameType }).nameType !== 'lastName';
          }
          return true;
        });
  }, [children, showLastName]);

  return (
    <div className={clsx(
      'grid grid-cols-1 md:grid-cols-auto gap-4',
      showLastName ? 'md:grid-cols-3' : 'md:grid-cols-2',
      className
    )}>
      {filteredChildren}
    </div>
  );
};

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  className?: string;
  error?: boolean;
  showCount?: boolean;
  maxLength?: number;
  type?: 'text' | 'number' | 'password' | 'email' | 'tel' | 'url' | 'date' | 'name';
  nameType?: NameType;
  min?: number;
  max?: number;
  step?: number;
  label?: string;
  helperText?: string;
  isRequired?: boolean;
  currencyCode?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ 
    className, 
    error, 
    showCount = false, 
    maxLength, 
    type = 'text', 
    nameType,
    min, 
    max, 
    step = 1, 
    value = '', 
    onChange, 
    disabled,
    label,
    helperText,
    isRequired = false,
    placeholder,
    currencyCode,
    id,
    ...props 
  }, ref) => {
    const [validationError, setValidationError] = useState<string | null>(null);
    const [interactionState, setInteractionState] = useState({ blurred: false, typed: false });
    const [showPassword, setShowPassword] = useState(false);

    const inputId = useMemo(() => id || `input-${Math.random().toString(36).substr(2, 9)}`, [id]);
    const currentLength = value.toString().length;

    const validate = useCallback((val: string): string | null => {
      if (!val) return isRequired ? `${type === 'email' ? 'Email' : type === 'url' ? 'URL' : type === 'password' ? 'Password' : 'Field'} is required` : null;
      
      if (type === 'email') {
        // RFC 5322 compliant email regex
        const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return emailRegex.test(val) ? null : 'Please enter a valid email address';
      }
      
      if (type === 'url') {
        try {
          const url = new URL(val);
          return ['http:', 'https:'].includes(url.protocol) ? null : 'Please enter a valid URL (e.g., https://www.example.com)';
        } catch {
          return 'Please enter a valid URL (e.g., https://www.example.com)';
        }
      }
      
      return null;
    }, [type, isRequired]);

    // Validate initial value for pre-populated fields
    useEffect(() => {
      const initialValue = value.toString();
      if (initialValue) {
        setValidationError(validate(initialValue));
      }
    }, [validate]); // Only run on mount and when validate function changes

    const handleNumericValue = useCallback((newValue: string): string => {
      if (newValue === '' || type !== 'number') return newValue;
      
      const numValue = Number(newValue);
      if (isNaN(numValue)) return value.toString();
      
      if (max !== undefined && numValue > max) return max.toString();
      if (min !== undefined && numValue < min) return min.toString();
      
      return newValue;
    }, [type, min, max, value]);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      if (!interactionState.typed) {
        setInteractionState(prev => ({ ...prev, typed: true }));
        setValidationError(null);
      }

      const adjustedValue = handleNumericValue(e.target.value);
      if (adjustedValue !== e.target.value) {
        e.target.value = adjustedValue;
      }

      onChange?.(e);
    }, [onChange, interactionState.typed, handleNumericValue]);

    const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
      setInteractionState(prev => ({ ...prev, blurred: true }));
      
      if (interactionState.typed || !interactionState.blurred) {
        setValidationError(validate(e.target.value));
      }

      props.onBlur?.(e);
    }, [validate, interactionState.typed, interactionState.blurred, props.onBlur]);

    const adjustNumericValue = useCallback((delta: number) => {
      if (disabled || type !== 'number' || !onChange) return;
      
      const currentValue = Number(value) || 0;
      const stepValue = currencyCode ? 1 : Number(step) || 1;
      const newValue = currentValue + (delta * stepValue);
      
      const constrainedValue = Math.max(
        min !== undefined ? min : -Infinity,
        Math.min(max !== undefined ? max : Infinity, newValue)
      );

      onChange({ target: { value: constrainedValue.toString() } } as React.ChangeEvent<HTMLInputElement>);
    }, [disabled, type, onChange, value, currencyCode, step, min, max]);

    const togglePasswordVisibility = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setShowPassword(prev => !prev);
    }, []);

    // Compute derived values
    const nameConfig = type === 'name' && nameType ? NAME_CONFIG[nameType] : null;
    const finalLabel = nameConfig?.label || label;
    const finalPlaceholder = nameConfig?.placeholder || placeholder || DEFAULT_PLACEHOLDERS[type as keyof typeof DEFAULT_PLACEHOLDERS] || '';
    const finalRequired = type === 'name' ? nameType !== 'middleName' && isRequired : isRequired;
    const currencySymbol = currencyCode && CURRENCIES[currencyCode as keyof typeof CURRENCIES]?.symbol;
    
    // Show validation errors for pre-populated fields or after user interaction
    const shouldShowError = error || validationError && (
      // Show error if user has interacted (typed and blurred)
      (interactionState.typed && interactionState.blurred) ||
      // Show error immediately for pre-populated invalid fields (value exists but user hasn't typed)
      (value.toString() && !interactionState.typed)
    );
    const displayError = shouldShowError ? (validationError || 'Error') : null;
    
    const ariaDescribedBy = [
      helperText && `${inputId}-helper`,
      displayError && `${inputId}-error`
    ].filter(Boolean).join(' ') || undefined;

    return (
      <div className="relative">
        {finalLabel && (
          <label htmlFor={inputId} className="block text-sm font-medium text-gray-700 mb-1">
            {finalLabel}
            {finalRequired && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        
        <div className="relative group">
          {currencySymbol && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" aria-hidden="true">
              {currencySymbol}
            </div>
          )}
          
          <input
            ref={ref}
            id={inputId}
            type={type === 'name' ? 'text' : type === 'password' && showPassword ? 'text' : type}
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            min={min}
            max={max}
            step={step}
            maxLength={maxLength}
            disabled={disabled}
            placeholder={finalPlaceholder}
            required={finalRequired}
            aria-required={finalRequired}
            aria-invalid={!!displayError}
            aria-describedby={ariaDescribedBy}
            aria-label={!finalLabel ? finalPlaceholder : undefined}
            data-name-type={nameType}
            className={clsx(
              'block w-full rounded-lg border border-gray-300 py-3 px-4 text-gray-900 shadow-xs focus:border-primary-base focus:outline-hidden focus:ring-1 focus:ring-primary-base text-bodyL',
              displayError
                ? 'border-red-300 placeholder:text-red-300 focus:border-red-500 focus:ring-red-500'
                : 'placeholder:text-gray-400',
              type === 'number' && 'pr-10',
              currencySymbol && 'pl-8',
              type === 'password' && 'pr-10',
              '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
              className
            )}
            {...props}
          />
          
          {type === 'number' && (
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex flex-col opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                type="button"
                onClick={() => adjustNumericValue(1)}
                aria-label="Increase value"
                className={clsx(
                  'p-0.5 text-gray-500 hover:text-primary-base focus:outline-hidden cursor-pointer',
                  (disabled || (max !== undefined && Number(value) >= max)) && 'opacity-50 cursor-not-allowed'
                )}
                disabled={disabled || (max !== undefined && Number(value) >= max)}
              >
                <ChevronUpIcon className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => adjustNumericValue(-1)}
                aria-label="Decrease value"
                className={clsx(
                  'p-0.5 text-gray-500 hover:text-primary-base focus:outline-hidden cursor-pointer',
                  (disabled || (min !== undefined && Number(value) <= min)) && 'opacity-50 cursor-not-allowed'
                )}
                disabled={disabled || (min !== undefined && Number(value) <= min)}
              >
                <ChevronDownIcon className="w-4 h-4" />
              </button>
            </div>
          )}
          
          {type === 'password' && (
            <button
              type="button"
              onClick={togglePasswordVisibility}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              className={clsx(
                'absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-primary-base focus:outline-hidden cursor-pointer',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
              disabled={disabled}
            >
              {showPassword ? <EyeIcon className="w-5 h-5" /> : <EyeSlashIcon className="w-5 h-5" />}
            </button>
          )}
          
          {showCount && maxLength && (
            <div className="absolute bottom-2 right-2 text-xs text-gray-500" aria-live="polite">
              {currentLength}/{maxLength}
            </div>
          )}
        </div>
        
        {helperText && !displayError && (
          <p id={`${inputId}-helper`} className="mt-2 text-sm text-gray-500">
            {helperText}
          </p>
        )}
        
        {displayError && (
          <p id={`${inputId}-error`} className="mt-2 text-sm text-red-500" role="alert" aria-live="polite">
            {displayError}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';